import React, { useRef, useEffect, useCallback } from 'react';

/* ═══════════════════════════════════════════════════════════════
   NETWORK ENGINE — Persistent Living Mobility Network
   
   6 layers:
   1. Ground Grid (depth perspective)
   2. Route Network (bezier lines)
   3. Ride Particles (glowing orbs on routes)
   4. Node Clusters (feature anchors)
   5. Atmosphere (grain, fog gradient)
   6. Camera System (scroll/cursor-linked)
   
   State machine driven by scroll position.
   ═══════════════════════════════════════════════════════════════ */

// ─── Scene State Types ───
type SceneState =
  | 'hero_idle'
  | 'hero_zoom'
  | 'features_active'
  | 'comparison'
  | 'values_morph'
  | 'perspective_split'
  | 'beta_focus'
  | 'auth_focus';

interface Camera {
  x: number;
  y: number;
  zoom: number;
  rotation: number;
  targetX: number;
  targetY: number;
  targetZoom: number;
  targetRotation: number;
}

interface RoutePoint {
  x: number;
  y: number;
}

interface Route {
  points: RoutePoint[];
  progress: number;
  targetProgress: number;
  color: string;
  width: number;
  glowIntensity: number;
}

interface Particle {
  routeIndex: number;
  t: number;
  speed: number;
  size: number;
  color: string;
  trail: { x: number; y: number; alpha: number }[];
}

interface NodeCluster {
  x: number;
  y: number;
  radius: number;
  glowIntensity: number;
  targetGlow: number;
  color: string;
  breathePhase: number;
  label: string;
}

// ─── Color Palette ───
const PALETTE = {
  gridLine: 'rgba(148, 163, 184, 0.06)',
  gridDot: 'rgba(94, 234, 212, 0.08)',
  routePrimary: '94, 234, 212',    // teal
  routeSecondary: '196, 181, 253', // lavender
  routeTertiary: '253, 164, 175',  // coral
  nodeTeal: 'rgba(94, 234, 212, 0.25)',
  nodeLavender: 'rgba(196, 181, 253, 0.25)',
  nodeCoral: 'rgba(253, 164, 175, 0.25)',
  particleTeal: '94, 234, 212',
  particleLavender: '196, 181, 253',
  fogTop: 'rgba(250, 250, 248, 0.6)',
  fogBottom: 'rgba(245, 243, 239, 0.8)',
};

// ─── Helpers ───
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function bezierPoint(pts: RoutePoint[], t: number): RoutePoint {
  if (pts.length < 2) return pts[0] || { x: 0, y: 0 };
  const totalSegs = pts.length - 1;
  const segF = t * totalSegs;
  const segIdx = Math.min(Math.floor(segF), totalSegs - 1);
  const localT = segF - segIdx;
  const p0 = pts[Math.max(0, segIdx - 1)];
  const p1 = pts[segIdx];
  const p2 = pts[Math.min(segIdx + 1, pts.length - 1)];
  const p3 = pts[Math.min(segIdx + 2, pts.length - 1)];
  const tx = 0.5 * (2 * p1.x + (-p0.x + p2.x) * localT +
    (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * localT * localT +
    (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * localT * localT * localT);
  const ty = 0.5 * (2 * p1.y + (-p0.y + p2.y) * localT +
    (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * localT * localT +
    (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * localT * localT * localT);
  return { x: tx, y: ty };
}

// ─── Component ───
export type NetworkEngineRef = {
  setState: (state: SceneState) => void;
  setAccentShift: (hue: 'teal' | 'lavender' | 'coral') => void;
};

const NetworkEngine = React.forwardRef<NetworkEngineRef>((_, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef<SceneState>('hero_idle');
  const accentRef = useRef<'teal' | 'lavender' | 'coral'>('teal');
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const scrollRef = useRef(0);
  const frameRef = useRef(0);

  // Expose imperative API
  React.useImperativeHandle(ref, () => ({
    setState: (s: SceneState) => { stateRef.current = s; },
    setAccentShift: (h: 'teal' | 'lavender' | 'coral') => { accentRef.current = h; },
  }));

  const handleMouseMove = useCallback((e: MouseEvent) => {
    mouseRef.current = {
      x: e.clientX / window.innerWidth,
      y: e.clientY / window.innerHeight,
    };
  }, []);

  const handleScroll = useCallback(() => {
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    scrollRef.current = maxScroll > 0 ? window.scrollY / maxScroll : 0;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // ─── Setup ───
    let animId: number;
    let w = 0;
    let h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    // ─── Camera ───
    const camera: Camera = {
      x: 0, y: 0, zoom: 1, rotation: 0,
      targetX: 0, targetY: 0, targetZoom: 1, targetRotation: 0,
    };

    // ─── Routes (generated in normalized 0-1 space) ───
    const generateRoutes = (): Route[] => {
      const routes: Route[] = [];
      const colors = [PALETTE.routePrimary, PALETTE.routeSecondary, PALETTE.routeTertiary];
      for (let i = 0; i < 12; i++) {
        const pts: RoutePoint[] = [];
        const segments = 4 + Math.floor(Math.random() * 3);
        let cx = Math.random() * 0.8 + 0.1;
        let cy = Math.random() * 0.8 + 0.1;
        for (let s = 0; s < segments; s++) {
          pts.push({ x: cx, y: cy });
          cx += (Math.random() - 0.5) * 0.3;
          cy += (Math.random() - 0.5) * 0.25;
          cx = Math.max(0.05, Math.min(0.95, cx));
          cy = Math.max(0.05, Math.min(0.95, cy));
        }
        routes.push({
          points: pts,
          progress: 0,
          targetProgress: 0.3 + Math.random() * 0.4,
          color: colors[i % 3],
          width: 1 + Math.random() * 0.8,
          glowIntensity: 0.3 + Math.random() * 0.3,
        });
      }
      return routes;
    };

    const routes = generateRoutes();

    // ─── Particles ───
    const particles: Particle[] = [];
    const spawnParticle = () => {
      if (particles.length >= 40) return;
      const ri = Math.floor(Math.random() * routes.length);
      particles.push({
        routeIndex: ri,
        t: Math.random(),
        speed: 0.001 + Math.random() * 0.002,
        size: 1.5 + Math.random() * 1.5,
        color: routes[ri].color,
        trail: [],
      });
    };
    for (let i = 0; i < 20; i++) spawnParticle();

    // ─── Nodes ───
    const nodes: NodeCluster[] = [
      { x: 0.18, y: 0.25, radius: 30, glowIntensity: 0, targetGlow: 0.3, color: PALETTE.nodeTeal, breathePhase: 0, label: 'Match' },
      { x: 0.45, y: 0.35, radius: 28, glowIntensity: 0, targetGlow: 0.3, color: PALETTE.nodeLavender, breathePhase: 1.2, label: 'Track' },
      { x: 0.72, y: 0.28, radius: 32, glowIntensity: 0, targetGlow: 0.3, color: PALETTE.nodeCoral, breathePhase: 2.4, label: 'Pay' },
      { x: 0.3, y: 0.65, radius: 26, glowIntensity: 0, targetGlow: 0.3, color: PALETTE.nodeTeal, breathePhase: 3.6, label: 'Rate' },
      { x: 0.65, y: 0.7, radius: 34, glowIntensity: 0, targetGlow: 0.3, color: PALETTE.nodeLavender, breathePhase: 4.8, label: 'Earn' },
    ];

    // ─── Grid ───
    const gridSpacing = 80;

    // ─── Main Loop ───
    const draw = () => {
      frameRef.current++;
      const t = Date.now() / 1000;
      const state = stateRef.current;
      const scroll = scrollRef.current;
      const mouse = mouseRef.current;

      // ── State-based targets ──
      switch (state) {
        case 'hero_idle':
          camera.targetZoom = 1;
          camera.targetRotation = 0;
          nodes.forEach(n => { n.targetGlow = 0.15; });
          routes.forEach(r => { r.targetProgress = 0.3 + Math.sin(t * 0.3) * 0.1; });
          break;
        case 'hero_zoom':
          camera.targetZoom = 1.08;
          nodes.forEach(n => { n.targetGlow = 0.3; });
          routes.forEach(r => { r.targetProgress = 0.6; });
          break;
        case 'features_active':
          camera.targetZoom = 1.04;
          camera.targetRotation = -0.005;
          nodes.forEach((n, i) => { n.targetGlow = i < 3 ? 0.7 : 0.2; });
          routes.forEach(r => { r.targetProgress = 0.8; });
          break;
        case 'comparison':
          camera.targetZoom = 1.02;
          routes.forEach(r => { r.targetProgress = 0.5 + scroll * 0.5; });
          break;
        case 'values_morph':
          camera.targetZoom = 1.06;
          camera.targetRotation = 0.003;
          nodes.forEach(n => { n.targetGlow = 0.5; });
          break;
        case 'perspective_split':
          camera.targetZoom = 1;
          break;
        case 'beta_focus':
          camera.targetZoom = 1.1;
          nodes.forEach(n => { n.targetGlow = 0.1; });
          routes.forEach(r => { r.targetProgress = 0.4; });
          break;
        case 'auth_focus':
          camera.targetZoom = 0.95;
          nodes.forEach(n => { n.targetGlow = 0.05; });
          routes.forEach(r => { r.targetProgress = 0.2; });
          break;
      }

      // Cursor influence on camera
      camera.targetX = (mouse.x - 0.5) * 15;
      camera.targetY = (mouse.y - 0.5) * 10;

      // Smooth camera lerp
      const cl = 0.03;
      camera.x = lerp(camera.x, camera.targetX, cl);
      camera.y = lerp(camera.y, camera.targetY, cl);
      camera.zoom = lerp(camera.zoom, camera.targetZoom, cl);
      camera.rotation = lerp(camera.rotation, camera.targetRotation, cl);

      // ── Clear ──
      ctx.clearRect(0, 0, w, h);

      // ── Apply Camera Transform ──
      ctx.save();
      ctx.translate(w / 2, h / 2);
      ctx.scale(camera.zoom, camera.zoom);
      ctx.rotate(camera.rotation);
      ctx.translate(-w / 2 + camera.x, -h / 2 + camera.y);

      // ── Layer 1: Ground Grid ──
      const drift = t * 3;
      for (let gx = -gridSpacing; gx <= w + gridSpacing; gx += gridSpacing) {
        const ox = ((gx + drift) % gridSpacing + gridSpacing) % gridSpacing - gridSpacing;
        for (let gy = -gridSpacing; gy <= h + gridSpacing; gy += gridSpacing) {
          ctx.beginPath();
          ctx.arc(ox + gx, gy, 1, 0, Math.PI * 2);
          const pulse = 0.5 + Math.sin(t * 1.5 + gx * 0.01 + gy * 0.01) * 0.3;
          ctx.fillStyle = `rgba(94, 234, 212, ${0.05 * pulse})`;
          ctx.fill();
        }
      }

      // Vertical grid lines
      for (let gx = 0; gx <= w; gx += gridSpacing) {
        const wave = Math.sin(t * 0.4 + gx * 0.003) * 2;
        ctx.beginPath();
        ctx.moveTo(gx + wave, 0);
        ctx.lineTo(gx - wave, h);
        ctx.strokeStyle = PALETTE.gridLine;
        ctx.lineWidth = 0.4;
        ctx.stroke();
      }
      // Horizontal grid lines
      for (let gy = 0; gy <= h; gy += gridSpacing) {
        const wave = Math.sin(t * 0.4 + gy * 0.003) * 2;
        ctx.beginPath();
        ctx.moveTo(0, gy + wave);
        ctx.lineTo(w, gy - wave);
        ctx.strokeStyle = 'rgba(148, 163, 184, 0.035)';
        ctx.lineWidth = 0.4;
        ctx.stroke();
      }

      // ── Layer 2: Route Network ──
      routes.forEach(route => {
        route.progress = lerp(route.progress, route.targetProgress, 0.02);
        if (route.progress < 0.01) return;

        const pts = route.points.map(p => ({ x: p.x * w, y: p.y * h }));
        const segments = 60;
        const drawSegs = Math.floor(segments * route.progress);

        // Glow pass
        ctx.save();
        ctx.globalCompositeOperation = 'lighter';
        ctx.beginPath();
        for (let s = 0; s <= drawSegs; s++) {
          const st = s / segments;
          const pt = bezierPoint(pts, st);
          if (s === 0) ctx.moveTo(pt.x, pt.y);
          else ctx.lineTo(pt.x, pt.y);
        }
        ctx.strokeStyle = `rgba(${route.color}, ${route.glowIntensity * 0.15})`;
        ctx.lineWidth = route.width * 6;
        ctx.lineCap = 'round';
        ctx.stroke();
        ctx.restore();

        // Main line
        ctx.beginPath();
        for (let s = 0; s <= drawSegs; s++) {
          const st = s / segments;
          const pt = bezierPoint(pts, st);
          if (s === 0) ctx.moveTo(pt.x, pt.y);
          else ctx.lineTo(pt.x, pt.y);
        }
        const alpha = 0.2 + route.glowIntensity * 0.3;
        ctx.strokeStyle = `rgba(${route.color}, ${alpha})`;
        ctx.lineWidth = route.width;
        ctx.lineCap = 'round';
        ctx.stroke();
      });

      // ── Layer 3: Ride Particles ──
      particles.forEach((p) => {
        const route = routes[p.routeIndex];
        if (!route || route.progress < 0.1) return;

        p.t += p.speed;
        if (p.t > route.progress) p.t = 0;

        const pts = route.points.map(pt => ({ x: pt.x * w, y: pt.y * h }));
        const pos = bezierPoint(pts, p.t);

        // Trail
        p.trail.unshift({ x: pos.x, y: pos.y, alpha: 1 });
        if (p.trail.length > 8) p.trail.pop();
        p.trail.forEach((tp, i) => {
          tp.alpha *= 0.85;
          if (tp.alpha > 0.02) {
            ctx.beginPath();
            ctx.arc(tp.x, tp.y, p.size * (1 - i * 0.1), 0, Math.PI * 2);
            ctx.fillStyle = `rgba(${p.color}, ${tp.alpha * 0.4})`;
            ctx.fill();
          }
        });

        // Head glow
        const headGlow = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, p.size * 4);
        headGlow.addColorStop(0, `rgba(${p.color}, 0.35)`);
        headGlow.addColorStop(1, `rgba(${p.color}, 0)`);
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, p.size * 4, 0, Math.PI * 2);
        ctx.fillStyle = headGlow;
        ctx.fill();

        // Core
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color}, 0.8)`;
        ctx.fill();
      });

      // Spawn new particles occasionally
      if (frameRef.current % 60 === 0 && particles.length < 30) {
        spawnParticle();
      }

      // ── Layer 4: Node Clusters ──
      nodes.forEach(node => {
        node.glowIntensity = lerp(node.glowIntensity, node.targetGlow, 0.02);
        const nx = node.x * w;
        const ny = node.y * h;
        const breathe = 1 + Math.sin(t * 1.2 + node.breathePhase) * 0.08;
        const r = node.radius * breathe;

        if (node.glowIntensity < 0.02) return;

        // Outer glow
        const grad = ctx.createRadialGradient(nx, ny, 0, nx, ny, r * 2.5);
        grad.addColorStop(0, node.color.replace('0.25', `${node.glowIntensity * 0.3}`));
        grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.beginPath();
        ctx.arc(nx, ny, r * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();

        // Core
        ctx.beginPath();
        ctx.arc(nx, ny, r * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = node.color.replace('0.25', `${node.glowIntensity * 0.6}`);
        ctx.fill();

        // Ring
        ctx.beginPath();
        ctx.arc(nx, ny, r, 0, Math.PI * 2);
        ctx.strokeStyle = node.color.replace('0.25', `${node.glowIntensity * 0.2}`);
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      ctx.restore(); // End camera transform

      // ── Layer 5: Atmosphere ──
      // Top fog
      const topGrad = ctx.createLinearGradient(0, 0, 0, h * 0.15);
      topGrad.addColorStop(0, 'rgba(250, 250, 248, 0.7)');
      topGrad.addColorStop(1, 'rgba(250, 250, 248, 0)');
      ctx.fillStyle = topGrad;
      ctx.fillRect(0, 0, w, h * 0.15);

      // Bottom fog
      const botGrad = ctx.createLinearGradient(0, h * 0.85, 0, h);
      botGrad.addColorStop(0, 'rgba(250, 250, 248, 0)');
      botGrad.addColorStop(1, 'rgba(250, 250, 248, 0.6)');
      ctx.fillStyle = botGrad;
      ctx.fillRect(0, h * 0.85, w, h * 0.15);

      animId = requestAnimationFrame(draw);
    };

    // ─── Events ───
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', resize);

    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', resize);
    };
  }, [handleMouseMove, handleScroll]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
});

NetworkEngine.displayName = 'NetworkEngine';
export default NetworkEngine;
